import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { ProgressSpinner } from "primereact/progressspinner";

import { useUserContext } from "./contexts/UserContext";

// Layout from Diamond template
import Layout from "./layout/layout";

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login/Login";

import AddOwner from "./pages/StationManagment/AddOwners/ManageOwners";
import Stations from "./pages/StationManagment/StationList/StationList";
import StationDetails from "./pages/StationManagment/StationDetails/StationDetails";

import Transactions from "./pages/StationManagment/Transactions/TransactionList";

import ClearingPage from "./pages/Clearing/ClearingPage";
import CreditNotesPage from "./pages/Clearing/CreditNotes/CreditNotesPage";
import BankTransactionsPage from "./pages/Clearing/BankTransactions/BankTransactionsPage";

import RoleRoute from "./components/RoleRoute";
import MovedTo from "./components/MovedTo";
import { isOnboardingEnabled } from "./config/featureFlags";
import EroundProvisioningPage from "./pages/Onboarding/EroundProvisioning/EroundProvisioningPage";

import DefaultPrices from "./pages/DefaultPrices/DefaultPrices";
import DefaultPricesEdit from "./pages/DefaultPrices/DefaultPricesEdit";

import CustomersPage from "./pages/Customer/CustomersPage";
import AddCustomer from "./pages/Customer/AddCustomer/AddCustomer";
import CustomerDetails from "./pages/Customer/CustomerDetails";
import CustomerEdit from "./pages/Customer/EditCustomer/CustomerEdit";
import CustomerStatisticsPage from "./pages/Customer/CustomerStatistics/CustomerStatisticsPage";

import EmpsPage from "./pages/Customer/EMPsPage";
import AddEMP from "./pages/Customer/AddEmp/AddEmp";
import EditEMP from "./pages/Customer/EditEMP/EditEMP";

import AppUserDetails from "./pages/AppUsers/AppUserDetails";
import AppUsersList from "./pages/AppUsers/AppUsersList";
import QrCodeEvseManagement from "./pages/QrCodeManagement/QrCodeEvseManagement";
import CommunityPrices from "./pages/CommunityPrices/CommunityPrices";
import CommunityPriceHistoryPage from "./pages/CommunityPrices/components/CommunityPriceHistory";
import { TransactionsPage } from "./pages/Transactions";
import RfidManagement from "./pages/RfidManagement/RfidManagement";
import TargetGroupsPage from "./pages/TargetGroups/TargetGroupsPage";
import GroupDetails from "./pages/TargetGroups/GroupDetails/GroupDetails";
import GroupEdit from "./pages/TargetGroups/GroupEdit/GroupEdit";
import GroupCreate from "./pages/TargetGroups/GroupCreate/GroupCreate";
import KpiDashboard from "./pages/KpiDashboard/KpiDashboard";
import StationPerformance from "./pages/StationPerformance/StationPerformance";
import ChargingStationsPage from "./pages/Analytics/ChargingStations/ChargingStationsPage";
import ThgAnnualSummary from "./pages/ThgAnnualSummary/ThgAnnualSummary";
import DatevExportsPage from "./pages/Accounting/DatevExportsPage";
import CampaignsPage from "./pages/Campaigns/CampaignsPage";
import WallboxFundingPage from "./pages/Campaigns/WallboxFunding/WallboxFundingPage";
import WallboxFundingDetailPage from "./pages/Campaigns/WallboxFunding/WallboxFundingDetailPage";
import AppVersionPage from "./pages/AppVersion/AppVersionPage";
import NotificationHistoryPage from "./pages/Alerting/NotificationHistory/NotificationHistoryPage";
import StationStatusDetailPage from "./pages/Alerting/StationStatusDetail/StationStatusDetailPage";
import AddStationExclusionsPage from "./pages/Alerting/StationExclusions/AddStationExclusionsPage";
import StationExclusionListPage from "./pages/Alerting/StationExclusions/StationExclusionListPage";
import StationStatusListPage from "./pages/Alerting/StationStatusList/StationStatusListPage";
import AiBudgetPage from "./pages/AiBudget/AiBudgetPage";
import AdminSubscriptionsPage from "./pages/Notifications/AdminSubscriptionsPage";
import SettingAuditPage from "./pages/Notifications/SettingAuditPage";
import ProspectsListPage from "./pages/Onboarding/Prospects/ProspectsListPage";
import ProspectDetailPage from "./pages/Onboarding/Prospects/ProspectDetailPage";
import WallboxCataloguePage from "./pages/Onboarding/WallboxCatalogue/WallboxCataloguePage";
import ResignReasonsPage from "./pages/Onboarding/ResignReasons/ResignReasonsPage";
import ContractAnalyticsPage from "./pages/Onboarding/ContractAnalytics/ContractAnalyticsPage";
import OnboardingAnalyticsPage from "./pages/Onboarding/Analytics/OnboardingAnalyticsPage";
import VendorConfigsPage from "./pages/Onboarding/VendorConfigs/VendorConfigsPage";
import PortalSettingsPage from "./pages/Onboarding/PortalSettings/PortalSettingsPage";
import WallboxOverlayPage from "./pages/Campaigns/WallboxOverlay/WallboxOverlayPage";

import WalletAdminPage from "./pages/WalletAdmin/WalletAdminPage";
import WalletDetailsPage from "./pages/WalletAdmin/WalletDetailsPage";

export default function AppRouter() {
  const { isLoading, isAuthorized } = useUserContext();

  if (isLoading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
      </div>
    );
  }

  if (!isAuthorized && !isLoading) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/add-owner/:id" element={<AddOwner />} />
          <Route path="/station-list" element={<Stations />} />
          <Route path="/station-details/:id" element={<StationDetails />} />
          <Route path="/clearing" element={<ClearingPage />} />
          <Route path="/clearing/credit-notes" element={<CreditNotesPage />} />
          <Route
            path="/clearing/bank-transactions"
            element={
              <RoleRoute role="bank">
                <BankTransactionsPage />
              </RoleRoute>
            }
          />
          <Route path="/transactions/:id" element={<Transactions />} />
          <Route path="/default-prices" element={<DefaultPrices />} />
          <Route path="/default-prices/edit" element={<DefaultPricesEdit />} />
          <Route path="/community-prices" element={<CommunityPrices />} />
          <Route
            path="/community-prices/history/:id"
            element={<CommunityPriceHistoryPage />}
          />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/add" element={<AddCustomer />} />
          <Route path="/customers/:id" element={<CustomerDetails />} />
          <Route path="/customers/:id/edit" element={<CustomerEdit />} />
          <Route
            path="/customers/:id/statistics"
            element={<CustomerStatisticsPage />}
          />
          <Route path="/customers/emps" element={<EmpsPage />} />
          <Route path="/customers/emps/add" element={<AddEMP />} />
          <Route path="/customers/emps/edit" element={<EditEMP />} />
          <Route path="/users" element={<AppUsersList />} />
          <Route path="/users/:emaid" element={<AppUserDetails />} />
          <Route path="/qr-codes" element={<QrCodeEvseManagement />} />
          <Route path="/rfid-cards" element={<RfidManagement />} />
          <Route path="/target-groups" element={<TargetGroupsPage />} />
          <Route path="/target-groups/list" element={<TargetGroupsPage />} />
          <Route path="/target-groups/observability/*" element={<TargetGroupsPage />} />
          <Route path="/target-groups/new" element={<GroupCreate />} />
          <Route path="/target-groups/:id/edit" element={<GroupEdit />} />
          <Route path="/target-groups/:id" element={<GroupDetails />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/operations" element={<KpiDashboard />} />
          {/* BOF-76: Analytics hosts the analytics screens. Station Performance moved here; its old address
              still works. */}
          <Route path="/analytics" element={<Navigate to="/analytics/station-performance" replace />} />
          <Route path="/analytics/station-performance" element={<StationPerformance />} />
          <Route path="/analytics/charging-stations" element={<ChargingStationsPage />} />
          <Route path="/station-performance" element={<MovedTo to="/analytics/station-performance" />} />
          {/* CLR-281: not wrapped in RoleRoute, so a non-admin sees why rather than being sent Home. */}
          <Route path="/thg-annual-summary" element={<ThgAnnualSummary />} />
          <Route
            path="/accounting/datev-exports"
            element={<DatevExportsPage />}
          />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route
            path="/campaigns/wallbox-funding"
            element={<WallboxFundingPage />}
          />
          <Route
            path="/campaigns/wallbox-funding/:id"
            element={<WallboxFundingDetailPage />}
          />
          <Route path="/campaigns/wallbox-overlay" element={<WallboxOverlayPage />} />
          <Route path="/app-version" element={<AppVersionPage />} />
          <Route
            path="/alerting/notifications/:locationId"
            element={<NotificationHistoryPage />}
          />
          <Route
            path="/alerting/status"
            element={
              <RoleRoute role="admin">
                <StationStatusListPage />
              </RoleRoute>
            }
          />
          <Route
            path="/alerting/status/:entityType/:entityId"
            element={
              <RoleRoute role="admin">
                <StationStatusDetailPage />
              </RoleRoute>
            }
          />
          <Route
            path="/alerting/exclusions"
            element={
              <RoleRoute role="admin">
                <StationExclusionListPage />
              </RoleRoute>
            }
          />
          <Route
            path="/alerting/exclusions/add"
            element={
              <RoleRoute role="admin">
                <AddStationExclusionsPage />
              </RoleRoute>
            }
          />
          <Route
            path="/notifications/subscriptions/:emaid?"
            element={
              <RoleRoute role="admin">
                <AdminSubscriptionsPage />
              </RoleRoute>
            }
          />
          <Route
            path="/notifications/audit/:emaid?"
            element={
              <RoleRoute role="admin">
                <SettingAuditPage />
              </RoleRoute>
            }
          />
          {/* Registered only when the onboarding flag is on. With it off a
              bookmarked onboarding address goes to the dashboard, the same
              place RoleRoute sends someone without the role. */}
          {isOnboardingEnabled ? (
            <>
              <Route
                path="/onboarding/analytics"
                element={<OnboardingAnalyticsPage />}
              />
              <Route
                path="/onboarding/prospects"
                element={<ProspectsListPage />}
              />
              <Route
                path="/onboarding/prospects/:id"
                element={<ProspectDetailPage />}
              />
              <Route
                path="/onboarding/wallbox"
                element={<WallboxCataloguePage />}
              />
              <Route
                path="/onboarding/eround-provisioning"
                element={<EroundProvisioningPage />}
              />
              <Route
                path="/onboarding/contract-analytics"
                element={<ContractAnalyticsPage />}
              />
              <Route
                path="/onboarding/resign-reasons"
                element={<ResignReasonsPage />}
              />
              <Route
                path="/onboarding/vendor-configs"
                element={<VendorConfigsPage />}
              />
              <Route
                path="/onboarding/portal-settings"
                element={<PortalSettingsPage />}
              />
            </>
          ) : (
            <Route path="/onboarding/*" element={<Navigate to="/" replace />} />
          )}
          <Route path="/ai-budget" element={<AiBudgetPage />} />
          <Route path="/wallet-admin" element={<WalletAdminPage />} />
          <Route path="/wallet-admin/:emaid" element={<WalletDetailsPage />} />
        </Routes>
      </Layout>
    </Router>
  );
}
